import React, { useState, useEffect } from 'react';
import { siteConfig, API_CONFIG } from './config';
import DateRangePicker from './components/DateRangePicker';
import SEOHead from './components/SEOHead';

const RoomsPage = ({ isDarkMode = false }) => {
  const [propertyData, setPropertyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [availabilityData, setAvailabilityData] = useState(null);
  const [showAmenitiesPopup, setShowAmenitiesPopup] = useState(false);
  const [selectedAmenities, setSelectedAmenities] = useState([]);
  const [popupTitle, setPopupTitle] = useState('');
  const [expandedDescriptions, setExpandedDescriptions] = useState({});

  // Fetch property data from API
  useEffect(() => {
    const fetchPropertyData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log('Fetching property data from:', API_CONFIG.contentUrl);
        
        const headers = {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Cache-Control': 'no-cache'
        };
        
        console.log('Making fetch request to:', API_CONFIG.contentUrl);
        console.log('With headers:', headers);
        
        const response = await fetch(API_CONFIG.contentUrl, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            bbid: siteConfig.propertyBBID.toString(),
            startdate: "2025-08-15", // Required by the proxy
            enddate: "2025-08-17" // Required by the proxy
          })
        });
        
        console.log('Response received:', {
          status: response.status,
          statusText: response.statusText,
          ok: response.ok,
          headers: Object.fromEntries(response.headers.entries())
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}`);
        }
        
        const data = await response.json();
        console.log('Property data received:', data);
        console.log('Property data structure:', {
          success: data.success,
          hasData: !!data.data,
          roomtypes: data.data?.roomtypes?.length || 0,
          propertyName: data.data?.name
        });
        // The API returns roomtypes directly in data, not in data.data
        setPropertyData(data.data);
      } catch (err) {
        console.error('Error fetching property data:', err);
        console.error('Error details:', {
          message: err.message,
          stack: err.stack,
          name: err.name
        });
        setError(`Failed to load property data: ${err.message}. Please try again.`);
      } finally {
        setLoading(false);
      }
    };

    fetchPropertyData();
  }, []);

  const handleAvailabilityChange = (data) => {
    setAvailabilityData(data);
  };

  const openAmenitiesPopup = (amenities, title) => {
    setSelectedAmenities(amenities);
    setPopupTitle(title);
    setShowAmenitiesPopup(true);
  };

  const truncateText = (text, wordLimit = 8) => {
    if (!text) return '';
    const words = text.split(' ');
    if (words.length <= wordLimit) return text;
    return words.slice(0, wordLimit).join(' ') + '...';
  };

  const toggleDescription = (roomTypeId) => {
    setExpandedDescriptions(prev => ({
      ...prev,
      [roomTypeId]: !prev[roomTypeId]
    }));
  };

  const handleRoomCardClick = (roomTypeId) => {
    if (availabilityData && !isRoomAvailable(roomTypeId)) {
      return; // Don't open booking modal if room is not available
    }
    
    console.log('Room card clicked for room:', roomTypeId);
    window.dispatchEvent(new CustomEvent('openBookingModal', {
      detail: { roomTypeId: roomTypeId }
    }));
  };

  const handleInteractiveElementClick = (e) => {
    e.stopPropagation(); // Prevent the room card click when clicking on interactive elements
  };

  const getRoomAvailability = (roomTypeId) => {
    if (!availabilityData || !availabilityData.data) return null;
    
    // Find the room availability in the API response
    // The data structure is now: data.roomtypes array
    const roomAvailability = availabilityData.data.roomtypes?.find(room => 
      room.rtid === roomTypeId || room.roomtypeid === roomTypeId
    );
    
    return roomAvailability;
  };

  const isRoomAvailable = (roomTypeId) => {
    const availability = getRoomAvailability(roomTypeId);
    if (!availability) return true; // Default to available if no data
    
    // Check if room is available based on API response structure
    if (availability.availability && Array.isArray(availability.availability)) {
      // Real API response structure: check if ALL days in the range are available
      const allDaysAvailable = availability.availability.every(day => day.roomsfree === true);
      console.log(`Room ${roomTypeId} availability check:`, {
        availability: availability.availability,
        allDaysAvailable: allDaysAvailable
      });
      return allDaysAvailable;
    } else if (availability.available !== undefined) {
      // Old mock data structure
      return availability.available !== false;
    }
    
    return true; // Default to available if no data
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen font-sans flex items-center justify-center" style={{ backgroundColor: '#f6f2ec' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading property information...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen font-sans flex items-center justify-center" style={{ backgroundColor: '#f6f2ec' }}>
        <div className="text-center max-w-md mx-auto px-4">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            <strong className="font-bold">Error loading data:</strong>
            <span className="block sm:inline"> {error}</span>
          </div>
          <p className="text-gray-600 mb-4">
            Unable to load property information. Please try again later.
          </p>
          <button 
            onClick={() => window.location.reload()} 
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // No data state
  if (!propertyData) {
    return (
      <div className="min-h-screen font-sans flex items-center justify-center" style={{ backgroundColor: '#f6f2ec' }}>
        <div className="text-center">
          <p className="text-gray-600">No property data available.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEOHead 
        title="Rooms & Accommodation"
        description="Explore our comfortable self-catering accommodation at J&B Lodge. Each room features mountain views, fully equipped kitchen, and modern amenities for your perfect Underberg getaway."
        keywords="J&B Lodge rooms, Underberg accommodation, self-catering rooms, mountain view rooms, South Africa accommodation"
        url="/rooms"
      />
      <div className={`min-h-screen font-sans ${isDarkMode ? 'bg-gray-900' : ''}`} style={!isDarkMode ? { backgroundColor: '#f6f2ec' } : {}}>
      {/* Hero Section */}
      <div className="relative h-[400px] w-full overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1571896349842-33c89424de2d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2068&q=80')"
          }}
        >
          <div className="absolute inset-0 bg-black bg-opacity-40"></div>
          <div className="relative h-full flex items-center justify-center">
            <div className="text-center text-white px-4">
              <h1 className="text-5xl md:text-6xl font-bold mb-4 leading-tight">
                Rooms and Availability
              </h1>
              <p className="text-xl md:text-2xl font-medium opacity-90">
                {siteConfig.siteDescription}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Property Information */}
      <div className="max-w-[80rem] mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Date Range Picker */}
        <DateRangePicker 
          onAvailabilityChange={handleAvailabilityChange} 
          propertyData={propertyData}
          isDarkMode={isDarkMode}
        />

        {/* Availability Notice */}
        {!availabilityData && (
          <div className={`border rounded-lg p-4 mb-8 ${isDarkMode ? 'bg-blue-900 border-blue-700' : 'bg-blue-50 border-blue-200'}`}>
            <div className="flex items-center">
              <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <span className={`text-sm ${isDarkMode ? 'text-blue-200' : 'text-blue-800'}`}>
                Select your dates above to check room availability
              </span>
            </div>
          </div>
        )}



      {/* Property Policies */}
        <div className="mt-12 bg-white rounded-lg shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Property Policies</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Check-in & Check-out</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• Check-in time: {propertyData.checkintime}</li>
                <li>• Check-out time: {propertyData.checkouttime}</li>
                <li>• Cut-off time: {propertyData.cutofftime}</li>
                <li>• Minimum advance booking: {propertyData.minadvancebookingdays} days</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">General Information</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• WiFi: {propertyData.wifi}</li>
                <li>• Parking: {propertyData.parking}</li>
                <li>• Smoking: {propertyData.allowsmoking ? 'Allowed' : 'Not allowed'}</li>
                <li>• Pets: {propertyData.allowpet}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Amenities Popup */}
      {showAmenitiesPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">{popupTitle}</h3>
              <button
                onClick={() => setShowAmenitiesPopup(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
            
            <div className="space-y-2">
              {selectedAmenities.map((amenity, index) => (
                <div key={index} className="flex items-center p-3 bg-gray-50 rounded-lg">
                  <svg className="w-4 h-4 text-green-500 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-700">{amenity.description}</span>
                </div>
              ))}
            </div>
            
            <div className="mt-6 pt-4 border-t border-gray-200">
              <button
                onClick={() => setShowAmenitiesPopup(false)}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </>
  );
};

export default RoomsPage; 